import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { GripVertical, Loader2, Lock, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

import apiService from '../services/api.service';
import { useAutoSave } from '../hooks/useAutoSave';
import AutoSaveIndicator from '../components/ui/AutoSaveIndicator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../components/ui/sheet';
import { UnderlineTabs } from '../components/ui/underline-tabs';
import { cn } from '../lib/utils';

/**
 * Parcours d'une course par parties (LOG-225).
 *
 * Une course se découpe en parties ordonnées ; chacune se termine par UNE
 * source : une cible sur un erg Concept2, le passage d'un tapis RFID, ou un
 * juge. Un groupe parallèle contient des parties qui courent en même temps.
 *
 * Sauvegarde automatique (cf. docs/features/auto-save.md) : chaque geste
 * envoie l'arbre COMPLET, l'ordre étant la donnée. Tant qu'une partie est
 * incomplète (une machine sans cible, un tapis non choisi), rien ne part :
 * le serveur refuserait le parcours entier, et l'écran le dit.
 *
 * Identifiants de glisser préfixés par leur conteneur (`top-…`, `grp-…`) :
 * leçon du planning des heats, une même clé dans deux listes casse le dnd.
 */

type PartType = 'concept2' | 'rfid' | 'judge' | 'group';
type Machine = 'row' | 'ski' | 'bike' | 'any';
type TargetKind = 'distance' | 'calories' | 'time';
type JudgeRole = 'judge' | 'head_judge' | 'race_director';
const MACHINES: Array<'row' | 'ski' | 'bike'> = ['row', 'ski', 'bike'];

interface Part {
  uid: string;
  id: string | null;
  name: string;
  part_type: PartType;
  machine: Machine | null;
  target_kind: TargetKind | null;
  targets: Partial<Record<'row' | 'ski' | 'bike', number>>;
  judge_role: JudgeRole | null;
  category_overrides: Record<string, Partial<Record<'row' | 'ski' | 'bike', number>>>;
  children: Part[];
}

interface Option { id: string; name: string; role?: string }

let uidSeq = 0;
const newUid = () => `p${uidSeq++}`;

function fromServer(p: any): Part {
  const overrides: Part['category_overrides'] = {};
  Object.entries(p.category_overrides || {}).forEach(([cat, o]: [string, any]) => {
    overrides[cat] = { ...(o?.targets || {}) };
  });
  return {
    uid: newUid(),
    id: p.id,
    name: p.name,
    part_type: p.part_type,
    machine: p.machine,
    target_kind: p.target_kind,
    targets: { ...(p.targets || {}) },
    judge_role: p.judge_role,
    category_overrides: overrides,
    children: (p.children || []).map(fromServer),
  };
}

function toServer(p: Part): any {
  return {
    id: p.id,
    name: p.name.trim(),
    part_type: p.part_type,
    machine: p.part_type === 'concept2' ? p.machine : null,
    target_kind: p.part_type === 'concept2' ? p.target_kind : null,
    targets: p.part_type === 'concept2' ? p.targets : null,
    judge_role: p.part_type === 'judge' ? (p.judge_role || 'judge') : null,
    category_overrides: p.part_type === 'concept2'
      ? Object.fromEntries(
          Object.entries(p.category_overrides)
            .filter(([, t]) => Object.values(t).some((v) => v && v > 0))
            .map(([cat, t]) => [cat, { targets: t }]),
        )
      : {},
    children: p.children.map(toServer),
  };
}

/** Machines dont la cible compte : celle de la partie, ou les trois si « n'importe laquelle ». */
const machinesOf = (p: Part) => (p.machine === 'any' ? MACHINES : p.machine ? [p.machine as 'row' | 'ski' | 'bike'] : []);

/** Ce qui manque pour qu'une partie soit enregistrable ; null si elle l'est. */
function missing(p: Part, t: (k: string) => string): string | null {
  if (!p.name.trim()) return t('courseParts.missing.name');
  if (p.part_type === 'group') {
    if (p.children.length < 2) return t('courseParts.missing.groupChildren');
    for (const c of p.children) {
      const m = missing(c, t);
      if (m) return m;
    }
    return null;
  }
  if (p.part_type === 'concept2') {
    if (!p.machine || !p.target_kind) return t('courseParts.missing.machine');
    const ms = machinesOf(p);
    if (!ms.every((m) => (p.targets[m] || 0) > 0)) {
      return t('courseParts.missing.target');
    }
  }
  return null;
}

function blank(type: PartType, name: string): Part {
  return {
    uid: newUid(), id: null, name, part_type: type,
    machine: type === 'concept2' ? 'row' : null,
    target_kind: type === 'concept2' ? 'distance' : null,
    targets: {},
    judge_role: type === 'judge' ? 'judge' : null,
    category_overrides: {}, children: [],
  };
}

function erg(machine: 'row' | 'ski' | 'bike', name: string, meters: number): Part {
  return { ...blank('concept2', name), machine, targets: { [machine]: meters } };
}

const unitOf = (kind: TargetKind | null) => (kind === 'calories' ? 'cal' : kind === 'time' ? 's' : 'm');

function fmtTargets(p: Part, t: (k: string) => string): string {
  const unit = unitOf(p.target_kind);
  return machinesOf(p)
    .filter((m) => p.targets[m])
    .map((m) => `${p.machine === 'any' ? `${t(`courseParts.machine.${m}`)} ` : ''}${p.targets[m]!.toLocaleString('fr-BE')} ${unit}`)
    .join(' · ');
}

/** Cibles affichées pour une catégorie : l'exception de la catégorie si elle existe. */
function targetsFor(p: Part, catId: string): Part['targets'] {
  if (catId === 'all' || p.part_type !== 'concept2' || p.machine === 'any') return p.targets;
  return { ...p.targets, ...(p.category_overrides[catId] || {}) };
}

interface SchemaProps {
  parts: Part[];
  catId: string;
  selected: string | null;
  locked: boolean;
  onOpen: (uid: string) => void;
  describe: (p: Part) => { chip: string; detail: string };
  t: (k: string) => string;
}

/**
 * Le parcours en schéma : Départ → parties → Arrivée, de gauche à droite sur
 * ordinateur, de haut en bas sur téléphone. Un groupe parallèle se divise en
 * branches qui se rejoignent. Il lit le même arbre que la liste, rien de plus.
 */
const CourseSchema: React.FC<SchemaProps> = ({ parts, catId, selected, locked, onOpen, describe, t }) => {
  const node = (p: Part, label: string, vertical: boolean) => {
    const miss = missing(p, t);
    const { chip, detail } = describe(p);
    const shown = p.part_type === 'concept2' ? fmtTargets({ ...p, targets: targetsFor(p, catId) }, t) : detail;
    return (
      <button
        key={p.uid}
        type="button"
        disabled={locked}
        onClick={() => onOpen(p.uid)}
        className={cn(
          'rounded-[5px] border bg-white/[0.02] px-3 py-2.5 text-left transition-colors enabled:hover:bg-white/[0.05]',
          vertical ? 'w-full' : 'w-[148px] shrink-0',
          selected === p.uid ? 'border-white/55 bg-white/[0.06]' : miss ? 'border-warning/60' : 'border-white/[0.14]',
        )}
      >
        <div className="font-anton text-[15px] leading-none text-muted-foreground">{label}</div>
        <div className="mt-1 truncate font-semibold">{p.name || t('courseParts.unnamed')}</div>
        <span className="mt-1.5 inline-block max-w-full truncate rounded-[2px] border border-white/[0.14] px-1.5 text-[11px] text-muted-foreground">{chip}</span>
        <div className={cn('mt-1.5 truncate text-xs tabular-nums', miss ? 'text-warning' : 'text-foreground')}>{miss || shown || '—'}</div>
      </button>
    );
  };
  const end = (finish: boolean, vertical: boolean) => (
    <div className={cn('flex shrink-0 items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground', !vertical && 'flex-col')}>
      <i className={cn('block h-3 w-3 rounded-full border-2 border-white/70', finish && 'bg-white')} />
      {t(finish ? 'courseParts.schema.finish' : 'courseParts.schema.start')}
    </div>
  );
  const link = <div className="h-0.5 w-5 shrink-0 bg-white/35" />;
  const vlink = <div className="ml-5 h-4 w-0.5 bg-white/35" />;

  return (
    <>
      {/* Ordinateur : de gauche à droite */}
      <div className="hidden items-center overflow-x-auto border-y border-white/[0.08] px-1 py-5 md:flex">
        {end(false, false)}
        {parts.map((p, i) => (
          <React.Fragment key={p.uid}>
            {link}
            {p.part_type === 'group' ? (
              <div className="flex shrink-0 items-stretch">
                <div className="relative w-4"><span className="absolute inset-y-[22%] right-0 w-0.5 bg-white/35" /><span className="absolute top-1/2 h-0.5 w-full bg-white/35" /></div>
                <div className="flex flex-col gap-2 rounded-[5px] border border-dashed border-white/[0.14] px-2.5 pb-2.5 pt-2">
                  <span className="whitespace-nowrap text-[11px] text-muted-foreground">{i + 1} · {t('courseParts.schema.together')}</span>
                  {p.children.map((c, j) => node(c, `${i + 1}${String.fromCharCode(97 + j)}`, false))}
                </div>
                <div className="relative w-4"><span className="absolute inset-y-[22%] left-0 w-0.5 bg-white/35" /><span className="absolute top-1/2 h-0.5 w-full bg-white/35" /></div>
              </div>
            ) : node(p, String(i + 1), false)}
          </React.Fragment>
        ))}
        {link}
        {end(true, false)}
      </div>

      {/* Téléphone : de haut en bas */}
      <div className="flex flex-col md:hidden">
        <div className="pl-3.5">{end(false, true)}</div>
        {parts.map((p, i) => (
          <React.Fragment key={p.uid}>
            {vlink}
            {p.part_type === 'group' ? (
              <div className="grid grid-cols-2 gap-2 rounded-[5px] border border-dashed border-white/[0.14] p-2">
                <span className="col-span-2 text-[11px] text-muted-foreground">{i + 1} · {t('courseParts.schema.together')}</span>
                {p.children.map((c, j) => node(c, `${i + 1}${String.fromCharCode(97 + j)}`, true))}
              </div>
            ) : node(p, String(i + 1), true)}
          </React.Fragment>
        ))}
        {vlink}
        <div className="pl-3.5">{end(true, true)}</div>
      </div>
    </>
  );
};

const Seg: React.FC<{ value: string | null; options: Array<{ value: string; label: string }>; onChange: (v: string) => void; disabled?: boolean }> = ({ value, options, onChange, disabled }) => (
  <div className="flex overflow-hidden rounded-[3px] border border-white/[0.14]">
    {options.map((o) => (
      <button
        key={o.value}
        type="button"
        disabled={disabled}
        onClick={() => onChange(o.value)}
        className={cn(
          'flex-1 border-r border-white/[0.14] px-2 py-1.5 text-xs last:border-r-0',
          value === o.value ? 'bg-white/[0.08] text-foreground' : 'text-muted-foreground hover:text-foreground',
        )}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const CourseParts: React.FC = () => {
  const { wodId } = useParams<{ wodId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [wodName, setWodName] = useState('');
  const [parts, setParts] = useState<Part[]>([]);
  const [lockedReason, setLockedReason] = useState<string | null>(null);
  const [categories, setCategories] = useState<Option[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [showExample, setShowExample] = useState(false);
  const [view, setView] = useState<'schema' | 'list'>('schema');
  const [catId, setCatId] = useState('all');
  const [stations, setStations] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const partsRef = useRef<Part[]>([]);
  partsRef.current = parts;
  const locked = !!lockedReason;

  const importStations = async () => {
    if (!wodId) return;
    setImporting(true);
    try {
      const data = await apiService.coursePartsFromStations(wodId);
      setParts((data.parts || []).map(fromServer));
      toast.success(t('courseParts.fromStations.done'));
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || t('courseParts.loadError'));
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    if (!wodId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiService.getCourseParts(wodId);
        if (cancelled) return;
        setWodName(data.wod_name || '');
        setParts((data.parts || []).map(fromServer));
        setLockedReason(data.locked_reason || null);
        setCategories(data.categories || []);
        setStations(data.stations || []);
      } catch (e: any) {
        toast.error(e?.response?.data?.detail || t('courseParts.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [wodId, t]);

  const incomplete = useMemo(() => {
    for (let i = 0; i < parts.length; i++) {
      const m = missing(parts[i], t);
      if (m) return { index: i + 1, reason: m };
    }
    return null;
  }, [parts, t]);

  const auto = useAutoSave<{ parts: Part[] }>(useCallback(async ({ parts: list }) => {
    if (!wodId) return;
    const data = await apiService.saveCourseParts(wodId, list.map(toServer));
    // Reporter les identifiants créés par le serveur, à leur place dans la
    // liste ENVOYÉE (l'écran a pu bouger depuis), sans remonter les champs.
    const ids = new Map<string, string>();
    const walk = (sent: Part[], got: any[]) => sent.forEach((p, i) => {
      if (got[i]?.id) ids.set(p.uid, got[i].id);
      walk(p.children, got[i]?.children || []);
    });
    walk(list, data.parts || []);
    const apply = (ps: Part[]): Part[] => ps.map((p) => ({ ...p, id: p.id || ids.get(p.uid) || null, children: apply(p.children) }));
    setParts((prev) => apply(prev));
  }, [wodId]), {
    onError: (e: any) => {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) setLockedReason(detail || t('courseParts.locked'));
      toast.error(typeof detail === 'string' ? detail : t('courseParts.saveError'));
    },
  });

  /** Écrit l'arbre, sauf s'il reste une partie incomplète. */
  const save = (list: Part[]) => {
    if (locked) return;
    for (const p of list) if (missing(p, t)) return;
    void auto.commit({ parts: list });
  };

  const change = (next: Part[], persist = true) => {
    setParts(next);
    if (persist) save(next);
  };

  const updatePart = (uid: string, patch: Partial<Part>, persist = true) => {
    const apply = (ps: Part[]): Part[] => ps.map((p) => (p.uid === uid ? { ...p, ...patch } : { ...p, children: apply(p.children) }));
    change(apply(partsRef.current), persist);
  };

  const removePart = (uid: string) => {
    const apply = (ps: Part[]): Part[] => ps.filter((p) => p.uid !== uid).map((p) => ({ ...p, children: apply(p.children) }));
    if (editing === uid) setEditing(null);
    change(apply(partsRef.current));
  };

  const addPart = (p: Part, openIt = true) => {
    change([...partsRef.current, p]);
    if (openIt) setEditing(p.uid);
  };

  const addToGroup = (groupUid: string) => {
    const child = blank('judge', t('courseParts.newPart'));
    const apply = (ps: Part[]): Part[] => ps.map((p) => (p.uid === groupUid ? { ...p, children: [...p.children, child] } : p));
    change(apply(partsRef.current));
    setEditing(child.uid);
  };

  const onDragEnd = (r: DropResult) => {
    if (!r.destination || locked) return;
    const from = r.source.droppableId;
    if (from !== r.destination.droppableId) return;
    const move = <T,>(list: T[]) => {
      const next = [...list];
      const [m] = next.splice(r.source.index, 1);
      next.splice(r.destination!.index, 0, m);
      return next;
    };
    if (from === 'top') change(move(partsRef.current));
    else {
      const groupUid = from.replace(/^grp-/, '');
      change(partsRef.current.map((p) => (p.uid === groupUid ? { ...p, children: move(p.children) } : p)));
    }
  };

  const applyTemplate = () => {
    if (partsRef.current.length && !window.confirm(t('courseParts.templateReplace'))) return;
    change([
      erg('row', 'Row', 1000),
      erg('ski', 'Ski', 1000),
      erg('bike', 'Bike', 2000),
    ]);
  };

  const flat = useMemo(() => {
    const out: Part[] = [];
    const walk = (ps: Part[]) => ps.forEach((p) => { out.push(p); walk(p.children); });
    walk(parts);
    return out;
  }, [parts]);
  const current = flat.find((p) => p.uid === editing) || null;
  const currentIsChild = !!current && !parts.some((p) => p.uid === current.uid);

  const describe = (p: Part): { chip: string; detail: string } => {
    if (p.part_type === 'concept2') {
      return {
        chip: `${t('courseParts.type.concept2Short')} · ${p.machine ? t(`courseParts.machine.${p.machine}`) : '—'}`,
        detail: fmtTargets(p, t),
      };
    }
    if (p.part_type === 'rfid') {
      return { chip: t('courseParts.type.rfidShort'), detail: t('courseParts.detail.rfid') };
    }
    if (p.part_type === 'judge') {
      const role = p.judge_role && p.judge_role !== 'judge' ? t(`courseParts.role.${p.judge_role}`) : null;
      return { chip: t('courseParts.type.judge'), detail: role || t('courseParts.typeHint.judge') };
    }
    return { chip: t('courseParts.type.group'), detail: t('courseParts.detail.group') };
  };

  const row = (p: Part, label: string, prefix: string, index: number) => {
    const { chip, detail } = describe(p);
    const miss = missing(p, t);
    return (
      <Draggable key={p.uid} draggableId={`${prefix}-${p.uid}`} index={index} isDragDisabled={locked}>
        {(drag, snap) => (
          <div
            ref={drag.innerRef}
            {...drag.draggableProps}
            className={cn(
              'grid grid-cols-[20px_36px_1fr_auto] items-center gap-3 border-b border-white/[0.08] px-1 py-3 md:grid-cols-[20px_36px_1fr_200px_220px_32px]',
              snap.isDragging && 'bg-white/[0.08] ring-1 ring-white/30',
              !locked && 'cursor-pointer hover:bg-white/[0.025]',
              editing === p.uid && 'bg-white/[0.05]',
            )}
            onClick={() => !locked && setEditing(p.uid)}
          >
            <span {...drag.dragHandleProps} onClick={(e) => e.stopPropagation()} className={cn('text-muted-foreground', locked ? 'opacity-0' : 'cursor-grab')}>
              <GripVertical size={16} />
            </span>
            <span className="font-anton text-xl text-muted-foreground">{label}</span>
            <div className="min-w-0">
              <div className="truncate font-semibold">{p.name || t('courseParts.unnamed')}</div>
              {miss && <div className="text-xs text-warning">{miss}</div>}
            </div>
            <span className="hidden max-w-full justify-self-start truncate rounded-[2px] border border-white/[0.14] px-2 py-0.5 text-xs text-muted-foreground md:inline-block">{chip}</span>
            <span className="hidden truncate text-sm tabular-nums text-muted-foreground md:block">{detail}</span>
            {!locked ? (
              <Button
                variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                aria-label={t('courseParts.remove')}
                onClick={(e) => { e.stopPropagation(); removePart(p.uid); }}
              >
                <Trash2 size={15} />
              </Button>
            ) : <span />}
          </div>
        )}
      </Draggable>
    );
  };

  if (loading) {
    return <div className="flex justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="mx-auto max-w-5xl p-4 md:p-6">
      <Button variant="link" size="sm" className="h-auto p-0 text-muted-foreground" onClick={() => navigate('/admin/wods')}>
        ← {t('courseParts.back')}
      </Button>
      <div className="mb-5 mt-1 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-anton text-4xl font-normal uppercase leading-none">{wodName}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{t('courseParts.formula')}</p>
        </div>
        <div className="flex items-center gap-3">
          <AutoSaveIndicator status={auto.status} retry={auto.retry} />
          {!locked && (
            <Button variant="outline" size="sm" onClick={applyTemplate}>{t('courseParts.templateRelay')}</Button>
          )}
        </div>
      </div>

      {locked && (
        <div className="mb-4 flex items-start gap-2 border-l-2 border-white/40 bg-white/[0.04] px-3 py-2 text-sm">
          <Lock size={15} className="mt-0.5 shrink-0" /> {lockedReason}
        </div>
      )}

      <UnderlineTabs
        className="mb-4"
        items={[{ value: 'schema', label: t('courseParts.schema.tab') }, { value: 'list', label: t('courseParts.schema.listTab') }]}
        value={view}
        onChange={(v) => setView(v as 'schema' | 'list')}
      />

      {incomplete && !locked && (
        <div className="mb-4 border-l-2 border-warning bg-warning/[0.06] px-3 py-2 text-sm">
          {t('courseParts.notSaved', { index: incomplete.index, reason: incomplete.reason })}
        </div>
      )}

      {parts.length === 0 && (
        <div className="border-y border-white/[0.08] py-8 text-center text-sm text-muted-foreground">
          <p>{t(locked ? 'courseParts.emptyLocked' : 'courseParts.empty')}</p>
          {!locked && stations.length > 0 && (
            <div className="mt-4 space-y-2">
              <Button variant="secondary" disabled={importing} onClick={importStations}>
                {t('courseParts.fromStations.button', { count: stations.length })}
              </Button>
              <p className="text-xs">{t('courseParts.fromStations.hint', { list: stations.join(' · ') })}</p>
            </div>
          )}
        </div>
      )}

      {view === 'schema' && parts.length > 0 && (
        <>
          {categories.length > 0 && (
            <UnderlineTabs
              size="sm"
              scrollable
              className="mb-3"
              aria-label={t('courseParts.schema.category')}
              items={[{ value: 'all', label: t('courseParts.schema.allCategories') }, ...categories.map((c) => ({ value: c.id, label: c.name }))]}
              value={catId}
              onChange={setCatId}
            />
          )}
          <CourseSchema parts={parts} catId={catId} selected={editing} locked={locked} onOpen={setEditing} describe={describe} t={t} />
        </>
      )}

      {view === 'list' && (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="top" type="top">
            {(drop) => (
              <div ref={drop.innerRef} {...drop.droppableProps} className={parts.length ? 'border-t border-white/[0.08]' : ''}>
                {parts.map((p, i) => (p.part_type === 'group' ? (
                  <Draggable key={p.uid} draggableId={`top-${p.uid}`} index={i} isDragDisabled={locked}>
                    {(drag, snap) => (
                      <div
                        ref={drag.innerRef}
                        {...drag.draggableProps}
                        className={cn('my-2 rounded-[5px] border border-dashed border-white/[0.14] px-2 pb-1 pt-1', snap.isDragging && 'bg-white/[0.06]')}
                      >
                        <div className="flex items-center gap-3 px-1 py-2 text-sm">
                          <span {...drag.dragHandleProps} className={cn('text-muted-foreground', locked ? 'opacity-0' : 'cursor-grab')}><GripVertical size={16} /></span>
                          <span className="font-anton text-xl text-muted-foreground">{i + 1}</span>
                          <button type="button" className="min-w-0 flex-1 text-left" disabled={locked} onClick={() => setEditing(p.uid)}>
                            <span className="font-semibold">{p.name}</span>
                            <span className="ml-2 text-muted-foreground">{t('courseParts.detail.group')}</span>
                          </button>
                          {!locked && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" aria-label={t('courseParts.remove')} onClick={() => removePart(p.uid)}>
                              <Trash2 size={15} />
                            </Button>
                          )}
                        </div>
                        <Droppable droppableId={`grp-${p.uid}`} type={`grp-${p.uid}`}>
                          {(inner) => (
                            <div ref={inner.innerRef} {...inner.droppableProps} className="pl-6">
                              {p.children.map((c, j) => row(c, `${i + 1}${String.fromCharCode(97 + j)}`, `grp-${p.uid}`, j))}
                              {inner.placeholder}
                            </div>
                          )}
                        </Droppable>
                        {!locked && (
                          <Button variant="ghost" size="sm" className="ml-6 mt-1 text-muted-foreground" onClick={() => addToGroup(p.uid)}>
                            + {t('courseParts.addToGroup')}
                          </Button>
                        )}
                      </div>
                    )}
                  </Draggable>
                ) : row(p, String(i + 1), 'top', i)))}
                {drop.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {!locked && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="default" onClick={() => addPart(blank('concept2', t('courseParts.newPart')))}>+ {t('courseParts.add')}</Button>
          <Button variant="ghost" onClick={() => addPart(erg('row', 'Row', 1000), false)}>+ Row 1 000 m</Button>
          <Button variant="ghost" onClick={() => addPart(blank('rfid', 'Run'), false)}>+ {t('courseParts.addRun')}</Button>
          <Button
            variant="ghost"
            onClick={() => {
              const g = blank('group', t('courseParts.newGroup'));
              g.children = [blank('judge', t('courseParts.partA')), blank('judge', t('courseParts.partB'))];
              addPart(g, false);
            }}
          >
            + {t('courseParts.addGroup')}
          </Button>
        </div>
      )}

      <div className="mt-6">
        <button type="button" className="text-left text-sm text-muted-foreground hover:text-foreground" onClick={() => setShowExample((v) => !v)}>
          {showExample ? '▾' : '▸'} {t('courseParts.exampleLink')}
        </button>
        {showExample && (
          <div className="mt-2 rounded-[5px] border border-dashed border-white/[0.14] bg-[repeating-linear-gradient(135deg,rgba(255,255,255,.025)_0_10px,transparent_10px_20px)] px-4 py-3 text-sm opacity-90">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t('courseParts.exampleTitle')}</div>
            {[
              ['1', 'Run 1 km', t('courseParts.type.rfidShort')],
              ['2', 'SkiErg 1 000 m', t('courseParts.type.concept2Short')],
              ['3', 'Sled Push', t('courseParts.type.rfidShort')],
              ['4a', 'Wall Balls · A', t('courseParts.type.judge')],
              ['4b', 'Burpees · B', t('courseParts.type.judge')],
            ].map(([n, name, kind]) => (
              <div key={n} className="grid grid-cols-[36px_1fr_auto] gap-3 border-b border-white/[0.06] py-2 last:border-0">
                <span className="font-anton text-muted-foreground">{n}</span><span>{name}</span><span className="text-muted-foreground">{kind}</span>
              </div>
            ))}
            <p className="mt-2 text-xs text-muted-foreground">{t('courseParts.exampleNote')}</p>
          </div>
        )}
      </div>

      <Sheet open={!!current} modal={false} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <SheetContent side="right" hideClose onInteractOutside={(e) => e.preventDefault()} className="flex w-full flex-col gap-0 p-0 shadow-2xl shadow-black/60 sm:max-w-[440px]">
          {current && (
            <>
              <div className="flex items-start gap-2 border-b border-white/[0.08] px-6 pb-4 pt-6">
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <SheetDescription className="text-xs">{t('courseParts.sheetHint')}</SheetDescription>
                  <SheetTitle className="truncate font-anton text-3xl font-normal uppercase leading-none">{current.name || t('courseParts.unnamed')}</SheetTitle>
                </div>
                <Button variant="ghost" size="icon" className="h-9 w-9" aria-label={t('common.close')} onClick={() => setEditing(null)}><X size={16} /></Button>
              </div>
              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-6">
                <label className="block">
                  <span className="mb-1.5 block text-xs text-muted-foreground">{t('courseParts.field.name')}</span>
                  <Input
                    value={current.name}
                    onChange={(e) => updatePart(current.uid, { name: e.target.value }, false)}
                    onBlur={() => save(partsRef.current)}
                  />
                </label>

                {current.part_type !== 'group' && (
                  <div>
                    <span className="mb-1.5 block text-xs text-muted-foreground">{t('courseParts.field.endsWith')}</span>
                    <div className="grid grid-cols-3 gap-2">
                      {(['concept2', 'rfid', 'judge'] as PartType[]).map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => updatePart(current.uid, {
                            part_type: type,
                            machine: type === 'concept2' ? current.machine || 'row' : null,
                            target_kind: type === 'concept2' ? current.target_kind || 'distance' : null,
                            judge_role: type === 'judge' ? current.judge_role || 'judge' : null,
                          })}
                          className={cn(
                            'rounded-[3px] border p-2 text-left disabled:opacity-40',
                            current.part_type === type ? 'border-white/55 bg-white/[0.05]' : 'border-white/[0.14]',
                          )}
                        >
                          <b className="block text-[13px]">{t(`courseParts.type.${type}`)}</b>
                          <small className="text-[11px] text-muted-foreground">{t(`courseParts.typeHint.${type}`)}</small>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {current.part_type === 'concept2' && (
                  <>
                    <div>
                      <span className="mb-1.5 block text-xs text-muted-foreground">{t('courseParts.field.machine')}</span>
                      <Seg
                        value={current.machine}
                        options={[...MACHINES, 'any'].map((m) => ({ value: m, label: t(`courseParts.machine.${m}`) }))}
                        onChange={(m) => {
                          const machine = m as Machine;
                          // Une cible déjà saisie suit la machine ; le Bike est
                          // prérempli au double des autres (décision du 2026-09-24).
                          const targets = { ...current.targets };
                          const base = targets.row || targets.ski || (targets.bike ? targets.bike / 2 : undefined);
                          if (base) {
                            for (const m of machine === 'any' ? MACHINES : [machine as 'row' | 'ski' | 'bike']) {
                              if (!targets[m]) targets[m] = m === 'bike' ? base * 2 : base;
                            }
                          }
                          // Une machine unique ne garde que sa cible.
                          const kept = machine === 'any' ? targets : { [machine]: targets[machine as 'row' | 'ski' | 'bike'] };
                          // Les cibles par catégorie suivent la même règle ; « au choix »
                          // n'en a pas (une par machine et par catégorie serait illisible).
                          const overrides: Part['category_overrides'] = {};
                          if (machine !== 'any') {
                            const mm = machine as 'row' | 'ski' | 'bike';
                            Object.entries(current.category_overrides).forEach(([cat, o]) => {
                              const [from, v] = Object.entries(o).find(([, x]) => x && x > 0) || [];
                              if (!from || !v) return;
                              const plain = from === 'bike' ? v / 2 : v;
                              overrides[cat] = { [mm]: mm === 'bike' ? plain * 2 : plain };
                            });
                          }
                          updatePart(current.uid, { machine, targets: kept, category_overrides: overrides });
                        }}
                      />
                    </div>
                    <div>
                      <span className="mb-1.5 block text-xs text-muted-foreground">{t('courseParts.field.target')}</span>
                      <Seg
                        value={current.target_kind}
                        options={(['distance', 'calories', 'time'] as TargetKind[]).map((k) => ({ value: k, label: t(`courseParts.targetKind.${k}`) }))}
                        onChange={(k) => updatePart(current.uid, { target_kind: k as TargetKind })}
                      />
                      <div className="mt-3 grid grid-cols-[70px_1fr_28px] items-center gap-2">
                        {machinesOf(current).map((m) => (
                          <React.Fragment key={m}>
                            <span className="text-sm text-muted-foreground">{t(`courseParts.machine.${m}`)}</span>
                            <NumberInput
                              min={1}
                              value={current.targets[m] ?? ''}
                              onChange={(e) => updatePart(current.uid, { targets: { ...current.targets, [m]: Number(e.target.value) } }, false)}
                              onBlur={() => save(partsRef.current)}
                            />
                            <span className="text-xs text-muted-foreground">{unitOf(current.target_kind)}</span>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                    {categories.length > 0 && (
                      <div>
                        <span className="mb-1.5 block text-xs text-muted-foreground">{t('courseParts.field.byCategory')}</span>
                        <div className="grid grid-cols-[1fr_110px_28px] items-center gap-2">
                          {categories.map((cat) => {
                            const m = machinesOf(current)[0];
                            if (!m || current.machine === 'any') return null;
                            const v = current.category_overrides[cat.id]?.[m];
                            return (
                              <React.Fragment key={cat.id}>
                                <span className="truncate text-sm text-muted-foreground">{cat.name}</span>
                                <NumberInput
                                  min={1}
                                  placeholder={current.targets[m] ? String(current.targets[m]) : ''}
                                  value={v ?? ''}
                                  onChange={(e) => updatePart(current.uid, {
                                    category_overrides: { ...current.category_overrides, [cat.id]: { [m]: Number(e.target.value) } },
                                  }, false)}
                                  onBlur={() => save(partsRef.current)}
                                />
                                <span className="text-xs text-muted-foreground">{unitOf(current.target_kind)}</span>
                              </React.Fragment>
                            );
                          })}
                        </div>
                        <p className="mt-1.5 text-xs text-muted-foreground">{t('courseParts.byCategoryHint')}</p>
                      </div>
                    )}
                  </>
                )}

                {current.part_type === 'rfid' && (
                  <p className="text-sm text-muted-foreground">{t('courseParts.matHint')}</p>
                )}

                {current.part_type === 'judge' && (
                  <div>
                    <span className="mb-1.5 block text-xs text-muted-foreground">{t('courseParts.field.role')}</span>
                    <Seg
                      value={current.judge_role}
                      options={(['judge', 'head_judge', 'race_director'] as JudgeRole[]).map((r) => ({ value: r, label: t(`courseParts.role.${r}`) }))}
                      onChange={(r) => updatePart(current.uid, { judge_role: r as JudgeRole })}
                    />
                  </div>
                )}

                {current.part_type === 'group' && (
                  <p className="text-sm text-muted-foreground">{t('courseParts.groupHint')}</p>
                )}

                {current.part_type !== 'judge' && current.part_type !== 'group' && (
                  <p className="border-t border-white/[0.08] pt-3 text-xs text-muted-foreground">{t('courseParts.judgeOverride')}</p>
                )}
                {currentIsChild && <p className="text-xs text-muted-foreground">{t('courseParts.inGroup')}</p>}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default CourseParts;
